import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  GripVertical, Plus, Trash2, ChevronDown, ChevronRight, 
  Save, X, Search, Sparkles, Settings, Eye, EyeOff,
  FileText, Hash, Calendar, Mail, Phone, Link, CheckSquare,
  List, MapPin, Users, DollarSign, Percent, RefreshCw, Check, Edit2, User,
  ArrowLeft, Building2, Star, Layers, Folder, Clock, CalendarPlus
} from 'lucide-react';
import { toast } from 'sonner';
import ModalTemplatesManager from './ModalTemplatesManager';
import getApiUrl from '../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const API_URL = getApiUrl();

// Liste des champs disponibles pour le header
const HEADER_FIELDS = [
  { id: 'nom', label: 'Nom' },
  { id: 'prenom', label: 'Prénom' },
  { id: 'id_qhare', label: 'ID Qhare' },
  { id: 'dpt', label: 'Département' },
  { id: 'logement', label: 'Type logement' },
  { id: 'statut', label: 'Statut' },
  { id: 'row_number', label: 'Numéro de ligne' },
  { id: 'tel_client', label: 'Téléphone' },
  { id: 'email', label: 'Email' },
  { id: 'ville', label: 'Ville' },
  { id: 'code_postal', label: 'Code postal' },
  { id: 'commercial', label: 'Commercial' },
];

// Types de champs disponibles
const FIELD_TYPES = {
  simple: [
    { id: 'text', label: 'Texte court', icon: FileText },
    { id: 'textarea', label: 'Texte long', icon: FileText },
    { id: 'number', label: 'Nombre', icon: Hash },
    { id: 'money', label: 'Montant (€)', icon: DollarSign },
    { id: 'percent', label: 'Pourcentage', icon: Percent },
    { id: 'date', label: 'Date', icon: Calendar },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'phone', label: 'Téléphone', icon: Phone },
    { id: 'url', label: 'Lien URL', icon: Link },
    { id: 'checkbox', label: 'Oui/Non', icon: CheckSquare },
    { id: 'select', label: 'Liste déroulante', icon: List },
  ],
  predictive: [
    { id: 'address', label: 'Adresse (Google)', icon: MapPin },
    { id: 'client', label: 'Client', icon: Users },
    { id: 'collaborator', label: 'Collaborateur', icon: Users },
    { id: 'artisan', label: 'Artisan', icon: Users },
  ]
};

// Composant Section Sortable
// Composant champ triable (drag and drop) avec édition du label
function SortableField({ field, sectionId, onToggleField, onDeleteField, onRenameField }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(field.label);
  const inputRef = useRef(null);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSaveLabel = () => {
    if (editLabel.trim() && editLabel !== field.label) {
      onRenameField(sectionId, field.id, editLabel.trim(), field.original_label || field.label);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSaveLabel();
    if (e.key === 'Escape') {
      setEditLabel(field.label);
      setIsEditing(false);
    }
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2 rounded ${field.enabled ? 'bg-slate-700/30' : 'bg-slate-800/30 opacity-50'} ${isDragging ? 'ring-1 ring-purple-500 z-10' : ''}`}
    >
      {/* Handle de drag */}
      <button 
        {...attributes} 
        {...listeners}
        className="p-1 cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300"
        title="Glisser pour réorganiser"
      >
        <GripVertical size={14} />
      </button>
      
      <button 
        onClick={() => onToggleField(sectionId, field.id)}
        className={`p-1 rounded ${field.enabled ? 'text-green-400' : 'text-slate-500'}`}
      >
        {field.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
      </button>
      
      {/* Label éditable */}
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editLabel}
          onChange={(e) => setEditLabel(e.target.value)}
          onBlur={handleSaveLabel}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-slate-600 border border-purple-500 rounded px-2 py-0.5 text-sm text-white"
        />
      ) : (
        <div className="flex-1 flex items-center gap-1 group">
          <span className="text-sm text-slate-300">{field.label}</span>
          {field.original_label && field.original_label !== field.label && (
            <span className="text-xs text-slate-500" title={`Nom original: ${field.original_label}`}>
              (renommé)
            </span>
          )}
          <button
            onClick={() => setIsEditing(true)}
            className="p-0.5 text-slate-500 hover:text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Renommer ce champ"
          >
            <Edit2 size={12} />
          </button>
        </div>
      )}
      
      {field.type === 'sheet' && field.fill_rate !== undefined && (
        <span className={`text-xs px-1.5 py-0.5 rounded ${
          field.fill_rate >= 80 ? 'bg-green-500/20 text-green-400' :
          field.fill_rate >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
          'bg-slate-600/50 text-slate-400'
        }`}>
          {field.fill_rate}%
        </span>
      )}
      
      {field.type === 'custom' && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
          Personnalisé
        </span>
      )}
      
      <button
        onClick={() => onDeleteField(sectionId, field.id)}
        className="p-1 text-slate-500 hover:text-red-400"
      >
        <X size={12} />
      </button>
    </div>
  );
}

// Wrapper pour le drag and drop des champs (évite les problèmes de hooks conditionnels)
function FieldsDndWrapper({ fields, sectionId, onMoveField, onToggleField, onDeleteField, onRenameField }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onMoveField(sectionId, active.id, over.id);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={fields.map(f => f.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="mt-2 space-y-1">
          {fields.map((field) => (
            <SortableField
              key={field.id}
              field={field}
              sectionId={sectionId}
              onToggleField={onToggleField}
              onDeleteField={onDeleteField}
              onRenameField={onRenameField}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableSection({ section, onToggle, onEdit, onDelete, onToggleField, onAddField, onDeleteField, onMoveField, onRenameField, isExpanded }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const enabledFieldsCount = section.fields?.filter(f => f.enabled).length || 0;
  const totalFieldsCount = section.fields?.length || 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-slate-800/50 rounded-lg border ${section.enabled ? 'border-slate-600' : 'border-slate-700/50 opacity-60'} mb-3`}
    >
      {/* Header de la section */}
      <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => onToggle(section.id)}>
        <button
          {...attributes}
          {...listeners}
          className="p-1 text-slate-400 hover:text-white cursor-grab active:cursor-grabbing"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical size={16} />
        </button>
        
        <span className="text-xl">{section.icon}</span>
        <span className="font-medium text-white flex-1">{section.label}</span>
        
        <span className="text-xs text-slate-400 mr-2">
          {enabledFieldsCount}/{totalFieldsCount} champs
        </span>
        
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(section); }}
          className="p-1 text-slate-400 hover:text-blue-400"
          title="Modifier"
        >
          <Settings size={14} />
        </button>
        
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(section.id); }}
          className="p-1 text-slate-400 hover:text-red-400"
          title="Supprimer"
        >
          <Trash2 size={14} />
        </button>
        
        {isExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </div>
      
      {/* Liste des champs avec drag and drop */}
      {isExpanded && (
        <div className="px-3 pb-3 border-t border-slate-700/50">
          <FieldsDndWrapper
            fields={section.fields || []}
            sectionId={section.id}
            onMoveField={onMoveField}
            onToggleField={onToggleField}
            onDeleteField={onDeleteField}
            onRenameField={onRenameField}
          />
          
          <button
            onClick={() => onAddField(section.id)}
            className="mt-2 w-full flex items-center justify-center gap-2 p-2 text-sm text-slate-400 hover:text-white hover:bg-slate-700/30 rounded border border-dashed border-slate-600"
          >
            <Plus size={14} />
            Ajouter un champ
          </button>
        </div>
      )}
    </div>
  );
}

// Modal d'ajout de section avec sélecteur de champs par catégories
function AddSectionModal({ isOpen, onClose, onSave, sheetColumns }) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('📋');
  const [iconSuggestions, setIconSuggestions] = useState(['📋', '📁', '📊', '📝', '🔧']);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingIcon, setIsLoadingIcon] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set(['Identité & Contact']));

  // Catégoriser automatiquement les colonnes par thème
  const categorizeColumns = useCallback((columns) => {
    const categories = {
      'Identité & Contact': [],
      'Adresse & Logement': [],
      'Commercial & Statut': [],
      'Finances & Aides': [],
      'Travaux & Devis': [],
      'Documents & Factures': [],
      'Dates': [],
      'Téléphonie & RDV': [],
      'Autres': []
    };

    const categoryKeywords = {
      'Identité & Contact': ['nom', 'prenom', 'prénom', 'civilit', 'email', 'mail', 'tel', 'téléphone', 'telephone', 'contact', 'client'],
      'Adresse & Logement': ['adresse', 'rue', 'ville', 'code postal', 'cp', 'dpt', 'département', 'logement', 'habitation', 'surface', 'chauffage', 'construction'],
      'Commercial & Statut': ['commercial', 'statut', 'état', 'etape', 'lead', 'source', 'telepro', 'téléprospect', 'qualif', 'transféré'],
      'Finances & Aides': ['rfr', 'revenu', 'aide', 'anah', 'mpr', 'prime', 'subvention', 'financement', 'montant', 'prix', 'encaiss', 'paiement', 'solde', 'rac', 'reste', 'amo', 'avance', 'dépôt', 'depot', 'octroi', 'enveloppe'],
      'Travaux & Devis': ['travaux', 'devis', 'chantier', 'artisan', 'pose', 'installation', 'intervention', 'fourni', 'réserve', 'reserve', 'levée', 'levee', 'pv'],
      'Documents & Factures': ['document', 'pièce', 'piece', 'rib', 'lettre', 'dal', 'attestation', 'certificat', 'justificatif', 'facture'],
      'Dates': ['date', 'délai', 'delai', 'création', 'creation', 'signature', 'fin'],
      'Téléphonie & RDV': ['rdv', 'rendez', 'appel', 'ringover', 'call', 'confirmation']
    };

    (columns || []).forEach(col => {
      const label = col.label.toLowerCase();
      let categorized = false;
      
      for (const [category, keywords] of Object.entries(categoryKeywords)) {
        if (keywords.some(kw => label.includes(kw))) {
          categories[category].push(col);
          categorized = true;
          break;
        }
      }
      
      if (!categorized) {
        categories['Autres'].push(col);
      }
    });

    // Supprimer les catégories vides
    return Object.fromEntries(
      Object.entries(categories).filter(([_, cols]) => cols.length > 0)
    );
  }, []);

  const categorizedColumns = categorizeColumns(sheetColumns);

  // Suggérer une icône via l'API
  const suggestIcon = useCallback(async () => {
    if (!name.trim()) return;
    
    setIsLoadingIcon(true);
    try {
      const response = await authFetch(`${API_URL}/api/config/suggest-icon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section_name: name })
      });
      const data = await response.json();
      setIcon(data.icon);
      setIconSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Erreur suggestion icône:', error);
    } finally {
      setIsLoadingIcon(false);
    }
  }, [name]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (name.trim()) suggestIcon();
    }, 500);
    return () => clearTimeout(timer);
  }, [name, suggestIcon]);

  // Filtrer par recherche
  const filterColumnsBySearch = (columns) => {
    if (!searchTerm) return columns;
    return columns.filter(col => 
      col.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleColumn = (col, checked) => {
    if (checked) {
      setSelectedColumns([...selectedColumns, col]);
    } else {
      setSelectedColumns(selectedColumns.filter(c => c.column !== col.column));
    }
  };

  const selectAllInCategory = (category) => {
    const cols = filterColumnsBySearch(categorizedColumns[category] || []);
    const existingColumns = new Set(selectedColumns.map(c => c.column));
    const newCols = cols.filter(c => !existingColumns.has(c.column));
    setSelectedColumns([...selectedColumns, ...newCols]);
  };

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Veuillez entrer un nom de section');
      return;
    }
    
    const fields = selectedColumns.map(col => ({
      id: `sheet_col_${col.column}`,
      label: col.label,
      type: 'sheet',
      column: col.column,
      enabled: true,
      fill_rate: col.fill_rate,
      fieldType: 'text'
    }));
    
    onSave({
      id: `section_${Date.now()}`,
      label: name,
      icon,
      enabled: true,
      collapsed: false,
      fields
    });
    
    // Reset
    setName('');
    setIcon('📋');
    setSelectedColumns([]);
    setSearchTerm('');
    onClose();
  };

  if (!isOpen) return null;

  // Icônes de catégories
  const categoryIcons = {
    'Identité & Contact': '👤',
    'Adresse & Logement': '🏠',
    'Commercial & Statut': '📊',
    'Finances & Aides': '💰',
    'Travaux & Devis': '🔧',
    'Documents & Factures': '📄',
    'Dates': '📅',
    'Téléphonie & RDV': '📞',
    'Autres': '📦'
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Plus size={20} />
            Nouvelle Section
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        
        <div className="flex flex-1 overflow-hidden">
          {/* Colonne gauche: Config de la section */}
          <div className="w-1/3 p-4 border-r border-slate-700 space-y-4">
            {/* Nom de la section */}
            <div>
              <label className="block text-sm text-slate-400 mb-1">Nom de la section *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Finances ANAH"
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500"
              />
            </div>
            
            {/* Icône */}
            <div>
              <label className="block text-sm text-slate-400 mb-1 flex items-center gap-2">
                Icône
                {isLoadingIcon && <Sparkles size={14} className="animate-pulse text-purple-400" />}
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="w-12 h-12 flex items-center justify-center bg-slate-700/50 rounded-lg text-2xl border border-slate-600">
                  {icon}
                </div>
                <div className="flex flex-wrap gap-1">
                  {iconSuggestions.map((ic, i) => (
                    <button
                      key={i}
                      onClick={() => setIcon(ic)}
                      className={`w-8 h-8 flex items-center justify-center rounded ${icon === ic ? 'bg-purple-500/30 ring-1 ring-purple-400' : 'bg-slate-700/50 hover:bg-slate-600'}`}
                    >
                      {ic}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Résumé des sélections */}
            {selectedColumns.length > 0 && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                <div className="text-sm font-medium text-purple-400 mb-2">
                  {selectedColumns.length} champ(s) sélectionné(s)
                </div>
                <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                  {selectedColumns.map(col => (
                    <span 
                      key={col.column}
                      className="inline-flex items-center gap-1 bg-slate-700 text-slate-300 text-xs px-2 py-1 rounded"
                    >
                      {col.label}
                      <button 
                        onClick={() => toggleColumn(col, false)}
                        className="text-slate-500 hover:text-red-400"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Colonne droite: Sélecteur de champs par catégories */}
          <div className="flex-1 p-4 flex flex-col">
            <div className="mb-3">
              <label className="block text-sm text-slate-400 mb-1">
                Champs disponibles ({sheetColumns?.length || 0} colonnes)
              </label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher un champ..."
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-9 pr-3 py-2 text-white placeholder-slate-500"
                />
              </div>
            </div>
            
            {/* Liste par catégories en accordéon */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {Object.entries(categorizedColumns).map(([category, columns]) => {
                const filteredCols = filterColumnsBySearch(columns);
                if (filteredCols.length === 0) return null;
                
                const isExpanded = expandedCategories.has(category);
                const selectedInCategory = filteredCols.filter(c => 
                  selectedColumns.some(sc => sc.column === c.column)
                ).length;
                
                return (
                  <div key={category} className="bg-slate-900/50 rounded-lg border border-slate-700 overflow-hidden">
                    {/* Header de catégorie */}
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between p-3 hover:bg-slate-700/30 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{categoryIcons[category] || '📦'}</span>
                        <span className="text-sm font-medium text-white">{category}</span>
                        <span className="text-xs text-slate-500">({filteredCols.length})</span>
                        {selectedInCategory > 0 && (
                          <span className="bg-purple-500/30 text-purple-400 text-xs px-1.5 py-0.5 rounded">
                            {selectedInCategory} sélectionné(s)
                          </span>
                        )}
                      </div>
                      <ChevronDown 
                        size={16} 
                        className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                      />
                    </button>
                    
                    {/* Contenu de la catégorie */}
                    {isExpanded && (
                      <div className="border-t border-slate-700">
                        {/* Bouton sélectionner tout */}
                        <button
                          onClick={() => selectAllInCategory(category)}
                          className="w-full text-left px-3 py-1.5 text-xs text-purple-400 hover:bg-purple-500/10"
                        >
                          + Tout sélectionner dans cette catégorie
                        </button>
                        
                        {/* Liste des champs */}
                        <div className="max-h-48 overflow-y-auto">
                          {filteredCols.map(col => (
                            <label
                              key={col.column}
                              className="flex items-center gap-2 px-3 py-2 hover:bg-slate-700/30 cursor-pointer border-t border-slate-700/50"
                            >
                              <input
                                type="checkbox"
                                checked={selectedColumns.some(c => c.column === col.column)}
                                onChange={(e) => toggleColumn(col, e.target.checked)}
                                className="rounded border-slate-600 bg-slate-700 text-purple-500"
                              />
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                col.fill_rate >= 80 ? 'bg-green-500/20 text-green-400' :
                                col.fill_rate >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-slate-600/50 text-slate-400'
                              }`}>
                                {Math.round(col.fill_rate)}%
                              </span>
                              <span className="text-sm text-slate-300 flex-1 truncate">{col.label}</span>
                              <span className="text-xs text-slate-500">col {col.column}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t border-slate-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-400 hover:text-white"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2"
          >
            <Plus size={16} />
            Créer la section ({selectedColumns.length} champs)
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal d'édition de section (renommer, changer icône)
function EditSectionModal({ isOpen, onClose, section, onSave }) {
  const [name, setName] = useState(section?.label || '');
  const [icon, setIcon] = useState(section?.icon || '📋');
  const [iconSuggestions, setIconSuggestions] = useState(['📋', '📁', '📊', '📝', '🔧', '💰', '📅', '👤', '🏠']);

  useEffect(() => {
    if (section) {
      setName(section.label || '');
      setIcon(section.icon || '📋');
    }
  }, [section]);

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Veuillez entrer un nom de section');
      return;
    }
    
    onSave(section.id, {
      label: name.trim(),
      icon
    });
    
    onClose();
    toast.success('Section modifiée');
  };

  if (!isOpen || !section) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl w-full max-w-md overflow-hidden">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Settings size={20} />
            Modifier la section
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Nom de la section */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Nom de la section *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Pipeline dates"
              className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500"
              autoFocus
            />
          </div>
          
          {/* Icône */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Icône</label>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="w-12 h-12 flex items-center justify-center bg-slate-700/50 rounded-lg text-2xl border border-slate-600">
                {icon}
              </div>
              <div className="flex flex-wrap gap-1">
                {iconSuggestions.map((ic, i) => (
                  <button
                    key={i}
                    onClick={() => setIcon(ic)}
                    className={`w-8 h-8 flex items-center justify-center rounded ${icon === ic ? 'bg-purple-500/30 ring-1 ring-purple-400' : 'bg-slate-700/50 hover:bg-slate-600'}`}
                  >
                    {ic}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t border-slate-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-400 hover:text-white"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg flex items-center gap-2"
          >
            <Check size={16} />
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal d'ajout de champ
function AddFieldModal({ isOpen, onClose, onSave, sectionId, sheetColumns, existingFieldIds, allUsedColumns = new Set() }) {
  const [fieldSource, setFieldSource] = useState('sheet'); // 'sheet' ou 'custom'
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [customFieldName, setCustomFieldName] = useState('');
  const [customFieldType, setCustomFieldType] = useState('text');
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOptions, setDropdownOptions] = useState(''); // Options pour liste déroulante

  // Afficher toutes les colonnes (ne plus filtrer les existantes - permettre la réutilisation)
  const availableColumns = sheetColumns?.filter(col => 
    col.label.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  
  // Vérifier si une colonne est déjà dans cette section
  const isColumnInSection = (col) => existingFieldIds.includes(`sheet_col_${col.column}`);
  
  // Vérifier si une colonne est utilisée ailleurs (dans d'autres sections)
  const isColumnUsedElsewhere = (col) => allUsedColumns.has(col.column) && !isColumnInSection(col);

  const handleSave = () => {
    if (fieldSource === 'sheet') {
      if (!selectedColumn) {
        toast.error('Veuillez sélectionner un champ');
        return;
      }
      onSave(sectionId, {
        id: `sheet_col_${selectedColumn.column}`,
        label: selectedColumn.label,
        type: 'sheet',
        column: selectedColumn.column,
        enabled: true,
        fill_rate: selectedColumn.fill_rate,
        fieldType: 'text'
      });
    } else {
      if (!customFieldName.trim()) {
        toast.error('Veuillez entrer un nom de champ');
        return;
      }
      
      // Validation des options pour liste déroulante
      if (customFieldType === 'select' && !dropdownOptions.trim()) {
        toast.error('Veuillez entrer les options de la liste déroulante');
        return;
      }
      
      const fieldData = {
        id: `custom_${Date.now()}`,
        label: customFieldName,
        type: 'custom',
        enabled: true,
        fieldType: customFieldType
      };
      
      // Ajouter les options si c'est une liste déroulante (avec support des groupes)
      if (customFieldType === 'select') {
        const lines = dropdownOptions.split('\n').map(opt => opt.trim()).filter(Boolean);
        const optionsWithGroups = [];
        let currentGroup = null;
        
        for (const line of lines) {
          if (line.startsWith('##')) {
            // C'est un titre de groupe
            currentGroup = line.replace('##', '').trim();
            optionsWithGroups.push({ type: 'group', label: currentGroup });
          } else {
            // C'est une option
            optionsWithGroups.push({ 
              type: 'option', 
              value: line, 
              label: line,
              group: currentGroup 
            });
          }
        }
        
        fieldData.options = optionsWithGroups;
        fieldData.hasGroups = optionsWithGroups.some(o => o.type === 'group');
      }
      
      onSave(sectionId, fieldData);
    }
    
    // Reset
    setSelectedColumn(null);
    setCustomFieldName('');
    setCustomFieldType('text');
    setSearchTerm('');
    setDropdownOptions('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Plus size={20} />
            Ajouter un champ
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Choix de la source */}
          <div className="flex gap-2">
            <button
              onClick={() => setFieldSource('sheet')}
              className={`flex-1 p-3 rounded-lg border ${fieldSource === 'sheet' ? 'bg-purple-500/20 border-purple-500 text-white' : 'bg-slate-700/30 border-slate-600 text-slate-400'}`}
            >
              <FileText size={20} className="mx-auto mb-1" />
              <div className="text-sm">Champ Google Sheet</div>
            </button>
            <button
              onClick={() => setFieldSource('custom')}
              className={`flex-1 p-3 rounded-lg border ${fieldSource === 'custom' ? 'bg-purple-500/20 border-purple-500 text-white' : 'bg-slate-700/30 border-slate-600 text-slate-400'}`}
            >
              <Sparkles size={20} className="mx-auto mb-1" />
              <div className="text-sm">Champ personnalisé</div>
            </button>
          </div>
          
          {fieldSource === 'sheet' ? (
            <>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher un champ..."
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-9 pr-3 py-2 text-white placeholder-slate-500"
                />
              </div>
              
              <div className="bg-slate-900/50 rounded-lg border border-slate-700 max-h-64 overflow-y-auto">
                {availableColumns.slice(0, 50).map(col => {
                  const alreadyInSection = isColumnInSection(col);
                  const usedElsewhere = isColumnUsedElsewhere(col);
                  return (
                    <label
                      key={col.column}
                      className={`flex items-center gap-2 p-2 cursor-pointer border-b border-slate-700/50 last:border-0 ${
                        selectedColumn?.column === col.column ? 'bg-purple-500/20' : 
                        alreadyInSection ? 'bg-orange-500/10 opacity-50' : 
                        usedElsewhere ? 'bg-blue-500/5' : 'hover:bg-slate-700/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="sheetColumn"
                        checked={selectedColumn?.column === col.column}
                        onChange={() => setSelectedColumn(col)}
                        className="text-purple-500"
                        disabled={alreadyInSection}
                      />
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        col.fill_rate >= 80 ? 'bg-green-500/20 text-green-400' :
                        col.fill_rate >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-slate-600/50 text-slate-400'
                      }`}>
                        {col.fill_rate}%
                      </span>
                      <span className={`text-sm flex-1 ${alreadyInSection ? 'text-slate-500' : 'text-slate-300'}`}>{col.label}</span>
                      {alreadyInSection && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">
                          Dans cette section
                        </span>
                      )}
                      {usedElsewhere && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                          Autre section
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Nom du champ *</label>
                <input
                  type="text"
                  value={customFieldName}
                  onChange={(e) => setCustomFieldName(e.target.value)}
                  placeholder="Ex: Note comptable interne"
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500"
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-2">Type de champ *</label>
                
                <div className="space-y-2">
                  <div className="text-xs text-slate-500 uppercase">Types simples</div>
                  <div className="grid grid-cols-3 gap-2">
                    {FIELD_TYPES.simple.map(type => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          onClick={() => setCustomFieldType(type.id)}
                          className={`p-2 rounded-lg border flex flex-col items-center gap-1 ${customFieldType === type.id ? 'bg-purple-500/20 border-purple-500 text-white' : 'bg-slate-700/30 border-slate-600 text-slate-400 hover:bg-slate-700/50'}`}
                        >
                          <Icon size={16} />
                          <span className="text-xs">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="text-xs text-slate-500 uppercase mt-3">Types prédictifs</div>
                  <div className="grid grid-cols-3 gap-2">
                    {FIELD_TYPES.predictive.map(type => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          onClick={() => setCustomFieldType(type.id)}
                          className={`p-2 rounded-lg border flex flex-col items-center gap-1 ${customFieldType === type.id ? 'bg-purple-500/20 border-purple-500 text-white' : 'bg-slate-700/30 border-slate-600 text-slate-400 hover:bg-slate-700/50'}`}
                        >
                          <Icon size={16} />
                          <span className="text-xs">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              {/* Options pour liste déroulante avec support des groupes */}
              {customFieldType === 'select' && (
                <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <label className="block text-sm text-purple-300 mb-2 flex items-center gap-2">
                    <List size={14} />
                    Options de la liste déroulante *
                  </label>
                  <textarea
                    value={dropdownOptions}
                    onChange={(e) => setDropdownOptions(e.target.value)}
                    placeholder="Utilisez ## pour créer des groupes :&#10;&#10;## Lien familial&#10;Conjoint(e)&#10;Fils&#10;Fille&#10;&#10;## Professionnel&#10;Assistant(e) social(e)&#10;Infirmier(ère)"
                    rows={8}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 text-sm font-mono"
                  />
                  
                  {/* Aperçu des options avec groupes */}
                  {dropdownOptions.trim() && (
                    <div className="mt-3 p-2 bg-slate-800/50 rounded-lg max-h-48 overflow-y-auto">
                      <p className="text-xs text-slate-400 mb-2">Aperçu :</p>
                      {dropdownOptions.split('\n').map((line, index) => {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) return null;
                        
                        if (trimmedLine.startsWith('##')) {
                          return (
                            <div key={index} className="text-xs font-semibold text-indigo-400 mt-2 mb-1 flex items-center gap-1">
                              <ChevronRight size={10} />
                              {trimmedLine.replace('##', '').trim()}
                            </div>
                          );
                        }
                        
                        return (
                          <div key={index} className="text-xs text-slate-300 pl-4 py-0.5 hover:bg-slate-700/30 rounded">
                            {trimmedLine}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-slate-500">
                      {dropdownOptions.split('\n').filter(o => o.trim() && !o.trim().startsWith('##')).length} option(s), 
                      {' '}{dropdownOptions.split('\n').filter(o => o.trim().startsWith('##')).length} groupe(s)
                    </p>
                    <p className="text-xs text-slate-600">
                      💡 Utilisez <code className="bg-slate-700 px-1 rounded">## Titre</code> pour créer un groupe
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="p-4 border-t border-slate-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white">
            Annuler
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg flex items-center gap-2"
          >
            <Plus size={16} />
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal de configuration du header
function HeaderConfigModal({ isOpen, onClose, headerConfig, onSave }) {
  const [titleFields, setTitleFields] = useState(headerConfig?.title_fields || []);
  const [subtitleFields, setSubtitleFields] = useState(headerConfig?.subtitle_fields || []);

  useEffect(() => {
    if (isOpen) {
      setTitleFields(headerConfig?.title_fields || []);
      setSubtitleFields(headerConfig?.subtitle_fields || []);
    }
  }, [isOpen, headerConfig]);

  if (!isOpen) return null;

  const toggleField = (fieldId, type) => {
    if (type === 'title') {
      setTitleFields(prev => 
        prev.includes(fieldId) 
          ? prev.filter(f => f !== fieldId) 
          : [...prev, fieldId]
      );
    } else {
      setSubtitleFields(prev => 
        prev.includes(fieldId) 
          ? prev.filter(f => f !== fieldId) 
          : [...prev, fieldId]
      );
    }
  };

  const handleSave = () => {
    onSave({ 
      ...headerConfig,
      title_fields: titleFields, 
      subtitle_fields: subtitleFields 
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl w-full max-w-lg border border-slate-700">
        <div className="p-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Edit2 size={20} className="text-purple-400" />
            Configurer le Header du Modal
          </h3>
          <p className="text-sm text-slate-400 mt-1">
            Choisissez les champs à afficher dans le titre et sous-titre
          </p>
        </div>

        <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
          {/* Champs du titre */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Champs du titre principal
            </label>
            <div className="flex flex-wrap gap-2">
              {HEADER_FIELDS.map(field => (
                <button
                  key={`title-${field.id}`}
                  onClick={() => toggleField(field.id, 'title')}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    titleFields.includes(field.id)
                      ? 'bg-purple-500 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {field.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Aperçu: {titleFields.map(f => HEADER_FIELDS.find(h => h.id === f)?.label).filter(Boolean).join(' ') || 'Aucun'}
            </p>
          </div>

          {/* Champs du sous-titre */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Champs du sous-titre
            </label>
            <div className="flex flex-wrap gap-2">
              {HEADER_FIELDS.map(field => (
                <button
                  key={`subtitle-${field.id}`}
                  onClick={() => toggleField(field.id, 'subtitle')}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    subtitleFields.includes(field.id)
                      ? 'bg-indigo-500 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {field.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Aperçu: {subtitleFields.map(f => HEADER_FIELDS.find(h => h.id === f)?.label).filter(Boolean).join(' • ') || 'Aucun'}
            </p>
          </div>
        </div>

        <div className="p-4 border-t border-slate-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg flex items-center gap-2"
          >
            <Check size={16} />
            Appliquer
          </button>
        </div>
      </div>
    </div>
  );
}

// Liste des onglets disponibles par défaut
const AVAILABLE_TABS = [
  { id: 'infos', label: 'Informations', icon: 'FileText', defaultEnabled: true },
  { id: 'exchanges', label: 'Échanges', icon: 'Mail', defaultEnabled: true },
  { id: 'history', label: 'Appels Tél', icon: 'Phone', defaultEnabled: true },
  { id: 'rdv-history', label: 'RDV', icon: 'Calendar', hasBadge: true, badgeField: 'rdv_count', defaultEnabled: true },
  { id: 'factures', label: 'Factures Fournisseurs', icon: 'Receipt', defaultEnabled: true },
  { id: 'encaissements', label: 'Encaissements', icon: 'DollarSign', defaultEnabled: true },
  { id: 'rdv', label: 'Prendre RDV', icon: 'CalendarPlus', defaultEnabled: true },
  { id: 'notes', label: 'Notes', icon: 'FileText', defaultEnabled: false },
  { id: 'documents', label: 'Documents', icon: 'Folder', defaultEnabled: false },
  { id: 'historique', label: 'Historique', icon: 'Clock', defaultEnabled: false },
];

// Modal de configuration des onglets dynamiques
function DynamicTabsConfigModal({ isOpen, onClose, tabs, onSave }) {
  const [localTabs, setLocalTabs] = useState([]);

  useEffect(() => {
    if (isOpen) {
      // Initialiser avec les tabs existants ou les defaults
      if (tabs && tabs.length > 0) {
        setLocalTabs([...tabs]);
      } else {
        setLocalTabs(AVAILABLE_TABS.map((tab, index) => ({
          ...tab,
          enabled: tab.defaultEnabled,
          order: index + 1,
          badge_field: tab.badgeField || null
        })));
      }
    }
  }, [isOpen, tabs]);

  const toggleTab = (tabId) => {
    setLocalTabs(prev => prev.map(t => 
      t.id === tabId ? { ...t, enabled: !t.enabled } : t
    ));
  };

  const moveTab = (tabId, direction) => {
    const index = localTabs.findIndex(t => t.id === tabId);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === localTabs.length - 1) return;

    const newTabs = [...localTabs];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newTabs[index], newTabs[swapIndex]] = [newTabs[swapIndex], newTabs[index]];
    
    // Réassigner les ordres
    newTabs.forEach((tab, i) => { tab.order = i + 1; });
    setLocalTabs(newTabs);
  };

  const handleSave = () => {
    onSave(localTabs);
    onClose();
  };

  if (!isOpen) return null;

  const enabledCount = localTabs.filter(t => t.enabled).length;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl w-full max-w-lg border border-slate-700 max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Layers size={20} className="text-indigo-400" />
            Configurer les Onglets
          </h3>
          <p className="text-sm text-slate-400 mt-1">
            Activez, désactivez et réordonnez les onglets du modal
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {localTabs.map((tab, index) => (
            <div
              key={tab.id}
              className={`p-3 rounded-lg border flex items-center gap-3 ${
                tab.enabled 
                  ? 'bg-indigo-500/10 border-indigo-500/30' 
                  : 'bg-slate-700/30 border-slate-700 opacity-60'
              }`}
            >
              {/* Boutons de réordonnancement */}
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => moveTab(tab.id, 'up')}
                  disabled={index === 0}
                  className="p-0.5 text-slate-400 hover:text-white disabled:opacity-30"
                >
                  <ChevronRight size={14} className="rotate-[-90deg]" />
                </button>
                <button
                  onClick={() => moveTab(tab.id, 'down')}
                  disabled={index === localTabs.length - 1}
                  className="p-0.5 text-slate-400 hover:text-white disabled:opacity-30"
                >
                  <ChevronRight size={14} className="rotate-90" />
                </button>
              </div>

              {/* Checkbox */}
              <input
                type="checkbox"
                checked={tab.enabled}
                onChange={() => toggleTab(tab.id)}
                className="w-4 h-4 rounded text-indigo-500"
              />

              {/* Info */}
              <div className="flex-1">
                <span className={`font-medium ${tab.enabled ? 'text-white' : 'text-slate-400'}`}>
                  {tab.label}
                </span>
                {tab.badge_field && (
                  <span className="ml-2 text-xs text-orange-400">(avec badge)</span>
                )}
              </div>

              {/* Ordre */}
              <span className="text-xs text-slate-500">#{index + 1}</span>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-slate-400">
              {enabledCount} onglet(s) actif(s)
            </span>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg flex items-center gap-2"
            >
              <Check size={16} />
              Appliquer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Données de test par défaut pour la prévisualisation
const DEFAULT_MOCK_DATA = {
  nom: "DUPONT",
  prenom: "Jean",
  tel_client: "0612345678",
  email: "jean.dupont@email.fr",
  adresse: "12 rue de la Paix",
  code_postal: "75001",
  ville: "Paris",
  dpt: "75",
  logement: "Propriétaire",
  statut: "Lead",
  row_number: 42,
  id_qhare: "QH-2025-001234",
  commercial: "Sophie Martin",
  rdv_count: 2
};

// Champs de base éditables dans la sidebar
const SIDEBAR_FIELDS = [
  { id: 'nom', label: 'Nom', type: 'text' },
  { id: 'prenom', label: 'Prénom', type: 'text' },
  { id: 'tel_client', label: 'Téléphone', type: 'text' },
  { id: 'email', label: 'Email', type: 'email' },
  { id: 'dpt', label: 'Département', type: 'text' },
  { id: 'statut', label: 'Statut', type: 'select', options: ['Lead', 'Qualifié', 'RDV Planifié', 'Devis Envoyé', 'Signé', 'En Travaux', 'Terminé'] },
];

// Composant champ adresse avec autocomplétion
function AddressFieldWithAutocomplete({ value, onChange, onAddressSelect }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  // Fermer les suggestions si clic en dehors
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
        if (isEditing) {
          onChange(editValue);
          setIsEditing(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing, editValue, onChange]);

  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }
    
    setLoading(true);
    try {
      // Utiliser le proxy backend pour éviter les problèmes CORS
      const response = await authFetch(
        `${API_URL}/api/address/search?q=${encodeURIComponent(query)}`
      );
      const data = await response.json();
      
      setSuggestions(data.suggestions || []);
      setShowSuggestions(true);
    } catch (err) {
      console.error("Erreur recherche adresse:", err);
    }
    setLoading(false);
  };

  const selectAddress = (suggestion) => {
    const newAdresse = suggestion.full || suggestion.adresse;
    setEditValue(newAdresse);
    onChange(newAdresse);
    
    // Mettre à jour aussi CP et Ville via le callback
    if (onAddressSelect) {
      onAddressSelect({
        adresse: newAdresse,
        code_postal: suggestion.cp,
        ville: suggestion.ville
      });
    }
    
    setShowSuggestions(false);
    setSuggestions([]);
    setIsEditing(false);
    toast.success('Adresse sélectionnée');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onChange(editValue);
      setIsEditing(false);
      setShowSuggestions(false);
    }
    if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
      setShowSuggestions(false);
    }
  };

  if (isEditing) {
    return (
      <div ref={containerRef} className="relative">
        <div className="relative">
          <MapPin className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value);
              searchAddress(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            className="w-full pl-8 pr-8 py-1 bg-slate-700 border border-emerald-500 rounded text-sm text-white placeholder-slate-500"
            placeholder="Tapez une adresse..."
          />
          {loading && (
            <RefreshCw className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
          )}
        </div>
        
        {/* Suggestions d'adresses */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => selectAddress(suggestion)}
                className="w-full px-3 py-2 text-left hover:bg-slate-700 border-b border-slate-700 last:border-b-0 transition-colors"
              >
                <p className="text-sm text-white">{suggestion.full || suggestion.adresse}</p>
                <p className="text-xs text-slate-400">{suggestion.cp} {suggestion.ville}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div 
      onClick={() => setIsEditing(true)}
      className="cursor-pointer group flex items-center gap-1"
    >
      <MapPin size={14} className="text-slate-500" />
      <span className={`text-sm ${value ? 'text-white' : 'text-slate-600 italic'}`}>
        {value || 'Cliquez pour saisir une adresse'}
      </span>
      <Edit2 size={12} className="text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

// Composant champ éditable inline
function InlineEditableField({ field, value, onChange, onAddressSelect }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const inputRef = useRef(null);

  // Détecter si c'est un champ adresse
  const isAddressField = field.id === 'adresse' || field.label?.toLowerCase().includes('adresse');

  // Ces hooks doivent être appelés avant toute condition de return
  useEffect(() => {
    if (!isAddressField) {
      setEditValue(value || '');
    }
  }, [value, isAddressField]);

  useEffect(() => {
    if (!isAddressField && isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [isEditing, isAddressField]);

  // Si c'est un champ adresse, utiliser le composant spécialisé
  if (isAddressField) {
    return (
      <AddressFieldWithAutocomplete 
        value={value} 
        onChange={onChange}
        onAddressSelect={onAddressSelect}
      />
    );
  }

  const handleSave = () => {
    onChange(editValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
    }
  };

  // Champ avec options (select ou select avec groupes)
  const hasOptions = field.fieldType === 'select' || field.options;
  const hasGroupedOptions = field.hasGroups && field.options;

  if (isEditing) {
    if (hasGroupedOptions) {
      // Select avec groupes
      const groups = {};
      let currentGroup = null;
      field.options.forEach(opt => {
        if (opt.type === 'group') {
          currentGroup = opt.label;
          groups[currentGroup] = [];
        } else if (currentGroup) {
          groups[currentGroup].push(opt);
        }
      });

      return (
        <div className="relative">
          <select
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            className="w-full bg-slate-700 border border-amber-500 rounded px-2 py-1 text-sm text-white"
          >
            <option value="">-- Sélectionner --</option>
            {Object.entries(groups).map(([groupName, options]) => (
              <optgroup key={groupName} label={groupName}>
                {options.map((opt, i) => (
                  <option key={i} value={opt.value || opt.label}>{opt.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      );
    } else if (hasOptions) {
      // Select simple
      const options = field.options || [];
      return (
        <select
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          className="w-full bg-slate-700 border border-amber-500 rounded px-2 py-1 text-sm text-white"
        >
          <option value="">-- Sélectionner --</option>
          {options.map((opt, i) => {
            const optValue = typeof opt === 'string' ? opt : (opt.value || opt.label);
            const optLabel = typeof opt === 'string' ? opt : opt.label;
            return <option key={i} value={optValue}>{optLabel}</option>;
          })}
        </select>
      );
    } else {
      // Input texte
      return (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="w-full bg-slate-700 border border-amber-500 rounded px-2 py-1 text-sm text-white"
        />
      );
    }
  }

  return (
    <div 
      onClick={() => setIsEditing(true)}
      className="cursor-pointer group flex items-center gap-1"
    >
      <span className={`text-sm ${value ? 'text-white' : 'text-slate-600 italic'}`}>
        {value || 'Non renseigné'}
      </span>
      <Edit2 size={12} className="text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

// Composant de prévisualisation du modal - Design identique au ClientModal principal
function ModalPreview({ isOpen, onClose, headerConfig, dynamicTabs, sections, templateName }) {
  const [activeTab, setActiveTab] = useState('infos');
  const [mockData, setMockData] = useState({ ...DEFAULT_MOCK_DATA });
  const [showSidebar, setShowSidebar] = useState(false);
  
  // Mode client réel
  const [useRealClient, setUseRealClient] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loadingClient, setLoadingClient] = useState(false);
  const [realClientData, setRealClientData] = useState(null);
  const [customFields, setCustomFields] = useState({});
  
  // Données des onglets pour le client réel
  const [clientRdvHistory, setClientRdvHistory] = useState([]);
  const [clientCalls, setClientCalls] = useState([]);
  const [clientFactures, setClientFactures] = useState([]);
  const [loadingTabData, setLoadingTabData] = useState(false);
  
  // Rechercher des clients via l'endpoint pipeline/clients avec paramètre search
  const searchClients = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setLoadingSearch(true);
    try {
      const response = await authFetch(`${API_URL}/api/pipeline/clients?search=${encodeURIComponent(query)}&limit=10`);
      const data = await response.json();
      setSearchResults(data.clients || []);
    } catch (error) {
      console.error('Erreur recherche clients:', error);
      setSearchResults([]);
    }
    setLoadingSearch(false);
  };
  
  // Charger les données d'un client réel
  const loadRealClient = async (client) => {
    setLoadingClient(true);
    setSelectedClient(client);
    
    try {
      // Charger les données du Sheet via l'endpoint by-row
      const response = await authFetch(`${API_URL}/api/pipeline/client/by-row/${encodeURIComponent(client.client_id || client.row_number)}`);
      const data = await response.json();
      
      if (data) {
        // L'endpoint retourne directement l'objet client
        const clientData = { ...data };
        
        // Aussi inclure les données client_details si présentes
        if (data.client_details) {
          Object.entries(data.client_details).forEach(([key, value]) => {
            if (value) clientData[key] = value;
          });
        }
        
        // IMPORTANT: Inclure les données brutes par colonne (raw_columns)
        // Les templates utilisent des IDs comme "sheet_col_20" pour les champs du Sheet
        if (data.raw_columns) {
          Object.entries(data.raw_columns).forEach(([key, value]) => {
            if (value) clientData[key] = value;
          });
        }
        
        // Normaliser les clés pour la correspondance avec les champs configurés
        Object.entries(clientData).forEach(([key, value]) => {
          const normalizedKey = key.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
          if (normalizedKey !== key) {
            clientData[normalizedKey] = value;
          }
        });
        
        setRealClientData(clientData);
        setMockData(clientData);
        
        // Charger les champs custom depuis MongoDB
        try {
          const customResponse = await authFetch(`${API_URL}/api/client-custom-fields/${encodeURIComponent(client.client_id || client.row_number)}`);
          const customData = await customResponse.json();
          if (customData.success && customData.custom_fields) {
            setCustomFields(customData.custom_fields);
            // Fusionner avec les données client
            setMockData(prev => ({ ...prev, ...customData.custom_fields }));
          }
        } catch (customError) {
          console.warn('Pas de champs custom pour ce client:', customError);
        }
        
        toast.success(`Client "${data.nom || client.nom} ${data.prenom || client.prenom}" chargé`);
        
        // Charger les données des onglets en parallèle
        // Essayer plusieurs champs pour trouver le téléphone
        const phone = data.tel_client || data.tel_qhare || data.telephone || 
                      (data.client_details && (data.client_details.tel_client || data.client_details.tel_qhare));
        loadClientTabData(client.row_number, phone);
      } else {
        toast.error('Client non trouvé');
      }
    } catch (error) {
      console.error('Erreur chargement client:', error);
      toast.error('Erreur lors du chargement du client');
    }
    
    setLoadingClient(false);
    setSearchResults([]);
    setSearchQuery('');
  };
  
  // Charger les données des onglets (RDV, appels, factures) pour un client
  const loadClientTabData = async (rowNumber, phone) => {
    setLoadingTabData(true);
    
    try {
      // Charger les RDV
      const rdvResponse = await authFetch(`${API_URL}/api/pipeline/client/${rowNumber}/rdv-history`);
      const rdvData = await rdvResponse.json();
      if (rdvData.rdv_history) {
        setClientRdvHistory(rdvData.rdv_history);
      }
      
      // Charger les appels si on a un numéro de téléphone
      if (phone) {
        const cleanPhone = phone.replace(/[^0-9]/g, '');
        const phoneWithPrefix = cleanPhone.startsWith('33') ? cleanPhone : (cleanPhone.startsWith('0') ? '33' + cleanPhone.slice(1) : cleanPhone);
        try {
          const callsResponse = await authFetch(`${API_URL}/api/ringover/calls/phone/${phoneWithPrefix}`);
          const callsData = await callsResponse.json();
          if (callsData.calls) {
            setClientCalls(callsData.calls);
          }
        } catch (e) {
          console.warn('Erreur chargement appels:', e);
        }
      }
      
      // Charger les factures
      try {
        const facturesResponse = await authFetch(`${API_URL}/api/clients/${rowNumber}/factures`);
        const facturesData = await facturesResponse.json();
        if (facturesData.factures) {
          setClientFactures(facturesData.factures);
        }
      } catch (e) {
        console.warn('Erreur chargement factures:', e);
      }
    } catch (error) {
      console.error('Erreur chargement données onglets:', error);
    }
    
    setLoadingTabData(false);
  };
  
  // Réinitialiser aux données de test
  const resetToTestData = () => {
    setMockData({ ...DEFAULT_MOCK_DATA });
    setRealClientData(null);
    setSelectedClient(null);
    setCustomFields({});
    setUseRealClient(false);
    // Réinitialiser les données des onglets
    setClientRdvHistory([]);
    setClientCalls([]);
    setClientFactures([]);
    toast.success('Retour aux données de test');
  };
  
  // Alias pour resetMockData (utilisé dans la sidebar)
  const resetMockData = () => {
    setMockData({ ...DEFAULT_MOCK_DATA });
    setSelectedClient(null);
    setRealClientData(null);
    if (!useRealClient) {
      toast.success('Données de test réinitialisées');
    }
  };
  
  // Debounce pour la recherche
  useEffect(() => {
    if (!useRealClient || !searchQuery) return;
    
    const timer = setTimeout(() => {
      searchClients(searchQuery);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, useRealClient]);
  
  // Mettre à jour un champ
  const updateField = (fieldId, value) => {
    setMockData(prev => ({ ...prev, [fieldId]: value }));
  };
  
  if (!isOpen) return null;

  // Construire le titre du header
  const getHeaderTitle = () => {
    return (headerConfig?.title_fields || ['nom', 'prenom'])
      .map(f => {
        // Chercher dans mockData avec différentes variantes de clé
        return mockData[f] || mockData[f.toUpperCase()] || mockData[`CLIENT ${f.toUpperCase()}`] || '';
      })
      .filter(Boolean)
      .join(' ') || 'Client Test';
  };

  // Construire le sous-titre du header
  const getHeaderSubtitle = () => {
    return (headerConfig?.subtitle_fields || [])
      .map(f => {
        const val = mockData[f] || mockData[f.toUpperCase()] || '';
        if (f === 'row_number') return val ? `Ligne ${val}` : '';
        if (f === 'dpt' && val) return `Dép. ${val}`;
        if (f === 'tel_client' && val) return `📞 ${val}`;
        if (f === 'logement') return val;
        return val;
      })
      .filter(Boolean)
      .join(' • ');
  };

  // Filtrer les onglets actifs
  const activeTabs = (dynamicTabs || []).filter(t => t.enabled).sort((a, b) => a.order - b.order);

  // Couleurs des titres de sections (alternance comme dans le modal original)
  const sectionColors = ['text-emerald-400', 'text-cyan-400', 'text-purple-400', 'text-blue-400', 'text-amber-400', 'text-pink-400', 'text-teal-400', 'text-orange-400'];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-slate-900 border border-slate-700 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex"
        onClick={e => e.stopPropagation()}
      >
        {/* CONTENU PRINCIPAL */}
        <div className="flex-1 flex flex-col min-w-0">
          
          {/* Bandeau prévisualisation */}
          <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-1.5 flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-400">
              <Eye size={14} />
              <span className="text-xs font-medium">Prévisualisation - {templateName}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-amber-400/60">Cliquez sur un champ pour le modifier</span>
              <button
                onClick={(e) => { e.stopPropagation(); setShowSidebar(!showSidebar); }}
                className={`p-1 rounded transition-colors ${showSidebar ? 'bg-amber-500 text-white' : 'text-amber-400 hover:bg-amber-500/20'}`}
                data-testid="toggle-sidebar-btn"
                title="Données de test"
              >
                <Settings size={12} />
              </button>
            </div>
          </div>
          
          {/* HEADER - Style identique au ClientModal */}
          <div className="flex items-center justify-between p-5 border-b border-slate-700 bg-slate-800">
            <div>
              <h2 className="text-xl font-bold text-white">{getHeaderTitle()}</h2>
              <p className="text-slate-400 text-sm">{getHeaderSubtitle()}</p>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* ONGLETS - Style identique au ClientModal */}
          <div className="flex border-b border-slate-700 bg-slate-850 overflow-x-auto">
            {activeTabs.length > 0 ? activeTabs.map(tab => (
              <button
                key={tab.id}
                onClick={(e) => { e.stopPropagation(); setActiveTab(tab.id); }}
                data-testid={`preview-tab-${tab.id}`}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'text-blue-400 border-b-2 border-blue-400 bg-slate-800/50' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/30'
                }`}
              >
                {tab.label}
                {tab.badge_field && mockData[tab.badge_field] > 0 && (
                  <span className="bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full text-xs">
                    {mockData[tab.badge_field]}
                  </span>
                )}
              </button>
            )) : (
              <span className="px-6 py-3 text-slate-500 italic text-sm">Aucun onglet configuré</span>
            )}
          </div>

          {/* CONTENU - Sections style identique au ClientModal */}
          <div className="flex-1 overflow-y-auto max-h-[calc(90vh-200px)]">
            <div className="p-5 space-y-6">
              {/* Onglet Informations - Affiche les sections configurées */}
              {activeTab === 'infos' && (
                <>
                  {sections && sections.length > 0 ? sections.filter(s => s.fields?.some(f => f.enabled)).map((section, sectionIndex) => (
                <div 
                  key={section.id} 
                  className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-slate-600 rounded-xl p-4"
                >
                  {/* Header de section - Style identique */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-sm font-bold ${sectionColors[sectionIndex % sectionColors.length]} uppercase tracking-wider flex items-center gap-2`}>
                      <span>{section.icon}</span>
                      {section.label}
                    </h3>
                  </div>
                  
                  {/* Grille de champs - Style identique */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.fields?.filter(f => f.enabled).map(field => {
                      const fieldKey = field.id || field.column;
                      const value = mockData[fieldKey] || '';
                      const isEditable = field.editable !== false;
                      const isAddressField = field.id === 'adresse' || field.label?.toLowerCase().includes('adresse');
                      
                      // Callback pour mettre à jour adresse + CP + ville
                      const handleAddressSelect = (addressData) => {
                        if (addressData.adresse) updateField('adresse', addressData.adresse);
                        if (addressData.code_postal) updateField('code_postal', addressData.code_postal);
                        if (addressData.ville) updateField('ville', addressData.ville);
                      };
                      
                      return (
                        <div key={field.id}>
                          <label className="block text-xs text-slate-400 mb-1">
                            {field.label}
                            {isAddressField && <span className="text-emerald-400 ml-1">(autocomplétion)</span>}
                          </label>
                          {isEditable ? (
                            <div 
                              onClick={(e) => e.stopPropagation()}
                              className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm hover:border-slate-500 transition-colors cursor-text"
                            >
                              <InlineEditableField 
                                field={field}
                                value={value}
                                onChange={(newValue) => updateField(fieldKey, newValue)}
                                onAddressSelect={handleAddressSelect}
                              />
                            </div>
                          ) : (
                            <div className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm">
                              {value || <span className="text-slate-600 italic">-</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )) : (
                <div className="text-center py-12 text-slate-400">
                  <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune section avec des champs actifs</p>
                  <p className="text-sm mt-1">Ajoutez des sections et activez des champs pour voir l'aperçu</p>
                </div>
              )}
                </>
              )}
              
              {/* Onglet Échanges */}
              {activeTab === 'exchanges' && (
                <div className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-slate-600 rounded-xl p-6">
                  <h3 className="text-sm font-bold text-cyan-400 uppercase tracking-wider flex items-center gap-2 mb-4">
                    <Mail size={16} />
                    Échanges
                  </h3>
                  {useRealClient && selectedClient ? (
                    <div className="text-slate-400 text-center py-8">
                      <Mail size={32} className="mx-auto mb-3 opacity-50" />
                      <p>Les échanges du client seront affichés ici</p>
                      <p className="text-sm text-slate-500 mt-1">Client: {selectedClient.nom} {selectedClient.prenom}</p>
                    </div>
                  ) : (
                    <div className="text-slate-500 text-center py-8 italic">
                      Chargez un client réel pour voir ses échanges
                    </div>
                  )}
                </div>
              )}
              
              {/* Onglet Appels Tél */}
              {activeTab === 'history' && (
                <div className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-slate-600 rounded-xl p-6">
                  <h3 className="text-sm font-bold text-purple-400 uppercase tracking-wider flex items-center gap-2 mb-4">
                    <Phone size={16} />
                    Historique des appels
                    {clientCalls.length > 0 && (
                      <span className="bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full text-xs ml-2">
                        {clientCalls.length}
                      </span>
                    )}
                  </h3>
                  {useRealClient && selectedClient ? (
                    loadingTabData ? (
                      <div className="text-center py-8">
                        <RefreshCw size={24} className="mx-auto mb-3 animate-spin text-purple-400" />
                        <p className="text-slate-400">Chargement des appels...</p>
                      </div>
                    ) : clientCalls.length > 0 ? (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {clientCalls.slice(0, 10).map((call, idx) => (
                          <div key={idx} className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${call.direction === 'in' ? 'bg-emerald-400' : 'bg-blue-400'}`} />
                                <span className="font-medium text-white">
                                  {call.direction === 'in' ? 'Appel entrant' : 'Appel sortant'}
                                </span>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded ${
                                call.is_answered ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                              }`}>
                                {call.is_answered ? 'Répondu' : 'Non répondu'}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-slate-500">Date:</span>
                                <span className="text-slate-300 ml-2">
                                  {new Date(call.start_time).toLocaleDateString('fr-FR')}
                                </span>
                              </div>
                              <div>
                                <span className="text-slate-500">Heure:</span>
                                <span className="text-slate-300 ml-2">
                                  {new Date(call.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              <div>
                                <span className="text-slate-500">Durée:</span>
                                <span className="text-slate-300 ml-2">
                                  {Math.floor((call.incall_duration || 0) / 60)}:{String((call.incall_duration || 0) % 60).padStart(2, '0')}
                                </span>
                              </div>
                              {call.ringover_user_name && (
                                <div>
                                  <span className="text-slate-500">Agent:</span>
                                  <span className="text-slate-300 ml-2">{call.crm_telepro || call.ringover_user_name}</span>
                                </div>
                              )}
                            </div>
                            {call.ai_summary && (
                              <div className="mt-2 p-2 bg-slate-800/50 rounded text-xs text-slate-400 italic">
                                {call.ai_summary.substring(0, 150)}...
                              </div>
                            )}
                          </div>
                        ))}
                        {clientCalls.length > 10 && (
                          <p className="text-center text-slate-500 text-sm">
                            + {clientCalls.length - 10} autres appels
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="text-slate-500 text-center py-8 italic">
                        Aucun appel trouvé pour ce client
                      </div>
                    )
                  ) : (
                    <div className="text-slate-500 text-center py-8 italic">
                      Chargez un client réel pour voir son historique d'appels
                    </div>
                  )}
                </div>
              )}
              
              {/* Onglet RDV */}
              {activeTab === 'rdv-history' && (
                <div className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-slate-600 rounded-xl p-6">
                  <h3 className="text-sm font-bold text-blue-400 uppercase tracking-wider flex items-center gap-2 mb-4">
                    <Calendar size={16} />
                    Rendez-vous
                    {clientRdvHistory.length > 0 && (
                      <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full text-xs ml-2">
                        {clientRdvHistory.length}
                      </span>
                    )}
                  </h3>
                  {useRealClient && selectedClient ? (
                    loadingTabData ? (
                      <div className="text-center py-8">
                        <RefreshCw size={24} className="mx-auto mb-3 animate-spin text-blue-400" />
                        <p className="text-slate-400">Chargement des RDV...</p>
                      </div>
                    ) : clientRdvHistory.length > 0 ? (
                      <div className="space-y-3">
                        {clientRdvHistory.map((rdv, idx) => (
                          <div key={idx} className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-white">{rdv.type_label || rdv.type}</span>
                              <span className={`text-xs px-2 py-1 rounded ${
                                rdv.statut === 'Effectué' ? 'bg-emerald-500/20 text-emerald-400' :
                                rdv.statut === "Aujourd'hui" ? 'bg-amber-500/20 text-amber-400' :
                                'bg-blue-500/20 text-blue-400'
                              }`}>
                                {rdv.statut}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-slate-500">Date RDV:</span>
                                <span className="text-slate-300 ml-2">{rdv.date_rdv || '-'}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Heure:</span>
                                <span className="text-slate-300 ml-2">{rdv.heure || '-'}</span>
                              </div>
                              {rdv.technicien && (
                                <div>
                                  <span className="text-slate-500">Technicien:</span>
                                  <span className="text-slate-300 ml-2">{rdv.technicien}</span>
                                </div>
                              )}
                              {rdv.date_prise_rdv && (
                                <div>
                                  <span className="text-slate-500">Prise RDV:</span>
                                  <span className="text-slate-300 ml-2">{rdv.date_prise_rdv}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-slate-500 text-center py-8 italic">
                        Aucun RDV trouvé pour ce client
                      </div>
                    )
                  ) : (
                    <div className="text-slate-500 text-center py-8 italic">
                      Chargez un client réel pour voir ses RDV
                    </div>
                  )}
                </div>
              )}
              
              {/* Onglet Factures Fournisseurs */}
              {activeTab === 'factures' && (
                <div className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-slate-600 rounded-xl p-6">
                  <h3 className="text-sm font-bold text-amber-400 uppercase tracking-wider flex items-center gap-2 mb-4">
                    <FileText size={16} />
                    Factures Fournisseurs
                  </h3>
                  {useRealClient && selectedClient ? (
                    <div className="text-slate-400 text-center py-8">
                      <FileText size={32} className="mx-auto mb-3 opacity-50" />
                      <p>Les factures fournisseurs seront affichées ici</p>
                      <p className="text-sm text-slate-500 mt-1">Client: {selectedClient.nom} {selectedClient.prenom}</p>
                    </div>
                  ) : (
                    <div className="text-slate-500 text-center py-8 italic">
                      Chargez un client réel pour voir ses factures
                    </div>
                  )}
                </div>
              )}
              
              {/* Onglet Encaissements */}
              {activeTab === 'encaissements' && (
                <div className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-slate-600 rounded-xl p-6">
                  <h3 className="text-sm font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-2 mb-4">
                    <DollarSign size={16} />
                    Encaissements
                  </h3>
                  {useRealClient && selectedClient ? (
                    <div className="text-slate-400 text-center py-8">
                      <DollarSign size={32} className="mx-auto mb-3 opacity-50" />
                      <p>Les encaissements seront affichés ici</p>
                      <p className="text-sm text-slate-500 mt-1">Client: {selectedClient.nom} {selectedClient.prenom}</p>
                    </div>
                  ) : (
                    <div className="text-slate-500 text-center py-8 italic">
                      Chargez un client réel pour voir ses encaissements
                    </div>
                  )}
                </div>
              )}
              
              {/* Onglet Prendre RDV */}
              {activeTab === 'prendre-rdv' && (
                <div className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-slate-600 rounded-xl p-6">
                  <h3 className="text-sm font-bold text-pink-400 uppercase tracking-wider flex items-center gap-2 mb-4">
                    <CalendarPlus size={16} />
                    Prendre RDV
                  </h3>
                  <div className="text-slate-400 text-center py-8">
                    <CalendarPlus size={32} className="mx-auto mb-3 opacity-50" />
                    <p>Formulaire de prise de RDV</p>
                    <p className="text-sm text-slate-500 mt-1">(Fonctionnalité de prévisualisation)</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SIDEBAR DROITE - Données de test ou Client réel */}
        {showSidebar && (
          <div 
            className="w-72 bg-slate-800 border-l border-slate-700 flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* En-tête avec toggle */}
            <div className="p-3 border-b border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                  {useRealClient ? 'Client réel' : 'Données test'}
                </h4>
                <button
                  onClick={useRealClient ? resetToTestData : resetMockData}
                  className="p-1 text-slate-400 hover:text-white rounded transition-colors"
                  title="Réinitialiser"
                >
                  <RefreshCw size={12} />
                </button>
              </div>
              
              {/* Toggle Mode */}
              <div className="flex items-center gap-2 p-2 bg-slate-900/50 rounded-lg">
                <button
                  onClick={() => {
                    setUseRealClient(false);
                    if (selectedClient) resetToTestData();
                  }}
                  className={`flex-1 py-1.5 px-2 rounded text-xs font-medium transition-all ${
                    !useRealClient 
                      ? 'bg-blue-500 text-white' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-700'
                  }`}
                  data-testid="mode-test-data-btn"
                >
                  Données test
                </button>
                <button
                  onClick={() => setUseRealClient(true)}
                  className={`flex-1 py-1.5 px-2 rounded text-xs font-medium transition-all ${
                    useRealClient 
                      ? 'bg-emerald-500 text-white' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-700'
                  }`}
                  data-testid="mode-real-client-btn"
                >
                  Client réel
                </button>
              </div>
            </div>
            
            {/* Contenu selon le mode */}
            <div className="flex-1 overflow-y-auto">
              {useRealClient ? (
                /* MODE CLIENT RÉEL */
                <div className="p-3 space-y-3">
                  {/* Recherche */}
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher un client..."
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
                      data-testid="real-client-search-input"
                    />
                    {loadingSearch && (
                      <RefreshCw size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-400 animate-spin" />
                    )}
                  </div>
                  
                  {/* Résultats de recherche */}
                  {searchResults.length > 0 && (
                    <div className="bg-slate-900/80 border border-slate-600 rounded-lg max-h-48 overflow-y-auto">
                      {searchResults.map((client, idx) => (
                        <button
                          key={client.row_number || idx}
                          onClick={() => loadRealClient(client)}
                          disabled={loadingClient}
                          className="w-full text-left px-3 py-2 hover:bg-emerald-500/10 border-b border-slate-700 last:border-0 transition-colors disabled:opacity-50"
                          data-testid={`search-result-${idx}`}
                        >
                          <div className="flex items-center gap-2">
                            <User size={14} className="text-emerald-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white font-medium truncate">
                                {client.nom} {client.prenom}
                              </p>
                              <p className="text-xs text-slate-500 truncate">
                                {client.dpt && `Dép. ${client.dpt}`}
                                {client.ville && ` • ${client.ville}`}
                                {client.row_number && ` • Ligne ${client.row_number}`}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Client sélectionné */}
                  {selectedClient && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <User size={16} className="text-emerald-400" />
                        <span className="text-sm font-medium text-emerald-400">Client chargé</span>
                      </div>
                      <p className="text-white font-medium">
                        {mockData.nom || selectedClient.nom} {mockData.prenom || selectedClient.prenom}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {mockData.dpt && `Dép. ${mockData.dpt}`}
                        {mockData.ville && ` • ${mockData.ville}`}
                        <br />
                        Ligne #{selectedClient.row_number}
                      </p>
                      {loadingClient && (
                        <div className="flex items-center gap-2 mt-2 text-emerald-400">
                          <RefreshCw size={12} className="animate-spin" />
                          <span className="text-xs">Chargement...</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Instructions */}
                  {!selectedClient && !searchQuery && (
                    <div className="text-center py-4 text-slate-500">
                      <Search size={24} className="mx-auto mb-2 opacity-50" />
                      <p className="text-xs">Recherchez un client par nom, prénom ou département</p>
                    </div>
                  )}
                </div>
              ) : (
                /* MODE DONNÉES TEST */
                <div className="p-3 space-y-3">
                  {SIDEBAR_FIELDS.map(field => (
                    <div key={field.id} className="space-y-1">
                      <label className="text-xs text-slate-500">{field.label}</label>
                      {field.type === 'select' ? (
                        <select
                          value={mockData[field.id] || ''}
                          onChange={(e) => updateField(field.id, e.target.value)}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-sm text-white focus:border-blue-500 focus:outline-none"
                        >
                          {field.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field.type}
                          value={mockData[field.id] || ''}
                          onChange={(e) => updateField(field.id, e.target.value)}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-sm text-white focus:border-blue-500 focus:outline-none"
                          placeholder={field.label}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Composant principal
export default function ClientFieldsConfigurator() {
  // Vue actuelle: 'templates' ou 'editor'
  const [view, setView] = useState('templates');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  const [modalType, setModalType] = useState('main'); // 'main' ou 'pipeline'
  const [sections, setSections] = useState([]);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [sheetColumns, setSheetColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Configuration du header
  const [headerConfig, setHeaderConfig] = useState({
    title_fields: ['nom', 'prenom'],
    subtitle_fields: ['dpt', 'logement', 'row_number'],
    gradient_from: 'purple-600',
    gradient_to: 'pink-600'
  });
  const [showHeaderConfig, setShowHeaderConfig] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  
  // Onglets dynamiques
  const [dynamicTabs, setDynamicTabs] = useState([]);
  const [showTabsConfig, setShowTabsConfig] = useState(false);
  
  // Prévisualisation
  const [showPreview, setShowPreview] = useState(false);
  
  // Modales
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [currentSectionForField, setCurrentSectionForField] = useState(null);
  const [showEditSectionModal, setShowEditSectionModal] = useState(false);
  const [sectionToEdit, setSectionToEdit] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sélectionner un template pour l'éditer
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setHeaderConfig(template.header || {
      title_fields: ['nom', 'prenom'],
      subtitle_fields: ['dpt', 'logement'],
      gradient_from: 'purple-600',
      gradient_to: 'pink-600'
    });
    setSections(template.sections || []);
    setDynamicTabs(template.dynamic_tabs || []);
    setModalType(template.type === 'pipeline' ? 'pipeline' : 'main');
    setHasChanges(false);
    setView('editor');
  };
  
  // Retourner à la liste des templates
  const handleBackToTemplates = () => {
    if (hasChanges) {
      if (!window.confirm('Vous avez des modifications non sauvegardées. Voulez-vous quitter ?')) {
        return;
      }
    }
    setView('templates');
    setSelectedTemplate(null);
  };

  // Charger la config d'un template
  useEffect(() => {
    if (view === 'editor' && selectedTemplate) {
      loadSheetColumns();
    }
  }, [view, selectedTemplate]);

  const loadConfig = async () => {
    setIsLoading(true);
    try {
      // Charger la config complète (sections + header)
      const response = await authFetch(`${API_URL}/api/config/modal-editor/${modalType}`);
      const data = await response.json();
      setSections(data.sections || []);
      
      // Charger le header si présent
      if (data.header) {
        setHeaderConfig(data.header);
      }
      
      setHasChanges(false);
    } catch (error) {
      console.error('Erreur chargement config:', error);
      // Fallback vers l'ancienne API
      try {
        const fallbackResp = await authFetch(`${API_URL}/api/config/client-modal/${modalType}`);
        const fallbackData = await fallbackResp.json();
        setSections(fallbackData.sections || []);
      } catch (e) {
        toast.error('Erreur lors du chargement de la configuration');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadSheetColumns = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/config/sheet-columns`);
      const data = await response.json();
      setSheetColumns(data.columns || []);
    } catch (error) {
      console.error('Erreur chargement colonnes:', error);
    }
  };

  // Mettre à jour une section (nom, icône)
  const updateSection = (sectionId, updates) => {
    setSections(prev => prev.map(s => 
      s.id === sectionId ? { ...s, ...updates } : s
    ));
    setHasChanges(true);
  };

  // Ouvrir le modal d'édition de section
  const openEditSectionModal = (section) => {
    setSectionToEdit(section);
    setShowEditSectionModal(true);
  };

  const saveConfig = async () => {
    setIsSaving(true);
    try {
      // Sauvegarder via l'API modal-editor qui inclut le header
      const response = await authFetch(`${API_URL}/api/config/modal-editor/${modalType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          header: headerConfig,
          sections 
        })
      });
      
      if (response.ok) {
        toast.success('Configuration sauvegardée');
        setHasChanges(false);
        // Mettre à jour le template sélectionné
        if (selectedTemplate) {
          setSelectedTemplate(prev => ({
            ...prev,
            header: headerConfig,
            sections,
            dynamic_tabs: dynamicTabs
          }));
        }
      } else {
        throw new Error('Erreur sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Sauvegarder le template (nouvelle méthode)
  const saveTemplate = async () => {
    if (!selectedTemplate) return;
    
    setIsSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/config/modal-templates/${selectedTemplate.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          header: headerConfig,
          sections,
          dynamic_tabs: dynamicTabs
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Template "${selectedTemplate.name}" sauvegardé`);
        setHasChanges(false);
        setSelectedTemplate(data.template);
      } else {
        throw new Error('Erreur sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde template:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  // Demander une suggestion IA
  const askAiSuggestion = async (target) => {
    if (aiSuggesting) return;
    
    setAiSuggesting(true);
    try {
      const response = await authFetch(`${API_URL}/api/config/ai-suggest-design`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target,
          current_config: { header: headerConfig, sections },
          available_fields: sheetColumns.map(col => ({ id: col.column?.toString(), label: col.label }))
        })
      });

      const data = await response.json();
      
      if (data.success && data.suggestion) {
        if (target === 'header' && data.suggestion.title_fields) {
          setHeaderConfig(prev => ({
            ...prev,
            title_fields: data.suggestion.title_fields,
            subtitle_fields: data.suggestion.subtitle_fields || prev.subtitle_fields
          }));
          toast.success("Suggestion IA appliquée au header !");
          setHasChanges(true);
        } else if (target === 'sections' && data.suggestion) {
          const rawSections = Array.isArray(data.suggestion) ? data.suggestion : data.suggestion.sections || [];
          if (rawSections.length > 0) {
            // Normaliser les sections IA pour avoir le bon format
            const normalizedSections = rawSections.map((section, index) => ({
              id: section.id || `ai_section_${Date.now()}_${index}`,
              label: section.label || 'Section IA',
              icon: section.icon || '✨',
              enabled: true,
              order: (sections?.length || 0) + index + 1,
              fields: (section.fields || []).map((field, fIndex) => {
                // Si field est une string, convertir en objet
                if (typeof field === 'string') {
                  return {
                    id: `ai_field_${field}_${Date.now()}_${fIndex}`,
                    label: field,
                    type: 'sheet',
                    enabled: true
                  };
                }
                // Sinon, s'assurer que le champ a un id valide
                return {
                  ...field,
                  id: field.id || `ai_field_${Date.now()}_${fIndex}`,
                  enabled: field.enabled !== false
                };
              })
            }));
            setSections(prev => [...prev, ...normalizedSections]);
            toast.success(`${normalizedSections.length} section(s) suggérée(s) ajoutée(s) !`);
            setHasChanges(true);
          }
        } else if (target === 'full' && data.suggestion.header) {
          setHeaderConfig(data.suggestion.header);
          if (data.suggestion.sections) {
            // Normaliser les sections IA
            const normalizedSections = data.suggestion.sections.map((section, index) => ({
              id: section.id || `ai_section_${Date.now()}_${index}`,
              label: section.label || 'Section IA',
              icon: section.icon || '✨',
              enabled: true,
              order: index + 1,
              fields: (section.fields || []).map((field, fIndex) => {
                if (typeof field === 'string') {
                  return {
                    id: `ai_field_${field}_${Date.now()}_${fIndex}`,
                    label: field,
                    type: 'sheet',
                    enabled: true
                  };
                }
                return {
                  ...field,
                  id: field.id || `ai_field_${Date.now()}_${fIndex}`,
                  enabled: field.enabled !== false
                };
              })
            }));
            setSections(normalizedSections);
          }
          toast.success("Suggestion IA appliquée !");
          setHasChanges(true);
        } else {
          toast.info("Suggestion appliquée");
        }
      } else {
        toast.info("Aucune suggestion disponible pour le moment");
      }
    } catch (error) {
      console.error("Erreur suggestion IA:", error);
      toast.error("Erreur lors de la suggestion IA");
    } finally {
      setAiSuggesting(false);
    }
  };

  // Mettre à jour la config du header
  const handleUpdateHeader = (newHeaderConfig) => {
    setHeaderConfig(newHeaderConfig);
    setHasChanges(true);
  };

  // Mettre à jour les onglets dynamiques
  const handleUpdateTabs = (newTabs) => {
    setDynamicTabs(newTabs);
    setHasChanges(true);
  };

  // Drag & Drop handler
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        // Mettre à jour les ordres
        return newItems.map((item, index) => ({ ...item, order: index + 1 }));
      });
      setHasChanges(true);
    }
  };

  // Toggle section expand
  const toggleSectionExpand = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Toggle field enabled
  const toggleField = (sectionId, fieldId) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          fields: section.fields.map(field => 
            field.id === fieldId ? { ...field, enabled: !field.enabled } : field
          )
        };
      }
      return section;
    }));
    setHasChanges(true);
  };

  // Supprimer une section
  const deleteSection = (sectionId) => {
    if (window.confirm('Supprimer cette section ?')) {
      setSections(prev => prev.filter(s => s.id !== sectionId));
      setHasChanges(true);
    }
  };

  // Supprimer un champ
  const deleteField = (sectionId, fieldId) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          fields: section.fields.filter(f => f.id !== fieldId)
        };
      }
      return section;
    }));
    setHasChanges(true);
  };

  // Ajouter une section
  const addSection = (newSection) => {
    setSections(prev => [...prev, { ...newSection, order: prev.length + 1 }]);
    setHasChanges(true);
  };

  // Ajouter un champ
  const addField = (sectionId, newField) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          fields: [...section.fields, newField]
        };
      }
      return section;
    }));
    setHasChanges(true);
  };

  // Déplacer un champ dans une section (drag and drop)
  const moveField = (sectionId, activeFieldId, overFieldId) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId && section.fields) {
        const oldIndex = section.fields.findIndex(f => f.id === activeFieldId);
        const newIndex = section.fields.findIndex(f => f.id === overFieldId);
        
        if (oldIndex !== -1 && newIndex !== -1) {
          const newFields = [...section.fields];
          const [movedField] = newFields.splice(oldIndex, 1);
          newFields.splice(newIndex, 0, movedField);
          
          return {
            ...section,
            fields: newFields
          };
        }
      }
      return section;
    }));
    setHasChanges(true);
  };

  // Renommer un champ (change le label affiché, garde l'id original)
  const renameField = async (sectionId, fieldId, newLabel, originalLabel) => {
    // Mettre à jour localement
    setSections(prev => prev.map(section => {
      if (section.id === sectionId && section.fields) {
        return {
          ...section,
          fields: section.fields.map(field => {
            if (field.id === fieldId) {
              return {
                ...field,
                label: newLabel,
                original_label: field.original_label || originalLabel // Garder le label original
              };
            }
            return field;
          })
        };
      }
      return section;
    }));
    setHasChanges(true);
    
    // Sauvegarder le mapping dans MongoDB pour référence
    try {
      await authFetch(`${API_URL}/api/config/field-label-mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          field_id: fieldId,
          original_label: originalLabel,
          display_label: newLabel,
          template_id: selectedTemplate?.id
        })
      });
      toast.success(`Champ renommé: "${originalLabel}" → "${newLabel}"`);
    } catch (error) {
      console.error('Erreur sauvegarde mapping:', error);
      // Le renommage local fonctionne même si le mapping échoue
    }
  };

  // Ouvrir le modal d'ajout de champ
  const openAddFieldModal = (sectionId) => {
    setCurrentSectionForField(sectionId);
    setShowAddFieldModal(true);
  };

  // Récupérer les IDs de champs existants pour une section
  const getExistingFieldIds = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    return section?.fields?.map(f => f.id) || [];
  };
  
  // Récupérer toutes les colonnes utilisées dans toutes les sections
  const getAllUsedColumns = () => {
    const usedColumns = new Set();
    sections.forEach(section => {
      section.fields?.forEach(field => {
        // Extraire le numéro de colonne, qu'il soit dans l'id ou dans le champ column
        if (field.column !== undefined && field.column !== null) {
          usedColumns.add(field.column);
        }
        // Aussi extraire depuis les IDs de type sheet_col_X
        if (field.id && field.id.startsWith('sheet_col_')) {
          const colNum = parseInt(field.id.replace('sheet_col_', ''));
          if (!isNaN(colNum)) usedColumns.add(colNum);
        }
      });
    });
    return usedColumns;
  };

  // Vue Templates : afficher la liste des templates
  if (view === 'templates') {
    return (
      <ModalTemplatesManager 
        onSelectTemplate={handleSelectTemplate}
        selectedTemplateId={selectedTemplate?.id}
      />
    );
  }

  // Vue Éditeur : éditer un template sélectionné
  return (
    <div className="space-y-4">
      {/* Header de l'éditeur avec bouton retour */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBackToTemplates}
            className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-white">{selectedTemplate?.name || 'Éditeur de Template'}</h2>
              {selectedTemplate?.is_default && (
                <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center gap-1">
                  <Star size={10} className="fill-current" />
                  Par défaut
                </span>
              )}
            </div>
            <p className="text-sm text-slate-400 capitalize">
              {selectedTemplate?.type === 'pipeline' ? 'Modal Pipeline' : 'Modal Principal'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPreview(true)}
            className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/50 text-amber-400 rounded-lg flex items-center gap-2"
            data-testid="preview-modal-btn"
          >
            <Eye size={16} />
            Prévisualiser
          </button>
          {hasChanges && (
            <button
              onClick={saveTemplate}
              disabled={isSaving}
              className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              data-testid="save-template-btn"
            >
              <Save size={16} />
              {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          )}
        </div>
      </div>

      {/* Configuration du Header */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className={`bg-gradient-to-r from-${headerConfig.gradient_from || 'purple-600'} to-${headerConfig.gradient_to || 'pink-600'} p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <User size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Configuration du Header</h3>
                <p className="text-sm text-white/70">
                  Titre: {headerConfig.title_fields?.map(f => HEADER_FIELDS.find(h => h.id === f)?.label).filter(Boolean).join(' ') || 'Non configuré'} | 
                  Sous-titre: {headerConfig.subtitle_fields?.map(f => HEADER_FIELDS.find(h => h.id === f)?.label).filter(Boolean).join(' • ') || 'Non configuré'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => askAiSuggestion('header')}
                disabled={aiSuggesting}
                className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm text-white flex items-center gap-2 disabled:opacity-50"
                title="Suggestion IA pour le header"
              >
                {aiSuggesting ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />}
                IA
              </button>
              <button
                onClick={() => setShowHeaderConfig(true)}
                className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm text-white flex items-center gap-2"
              >
                <Edit2 size={14} />
                Éditer
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration des Sélections Dynamiques (Onglets) */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <Layers size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Sélections Dynamiques (Onglets)</h3>
                <p className="text-sm text-white/70">
                  {dynamicTabs.filter(t => t.enabled).length} onglet(s) actif(s) sur {dynamicTabs.length}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowTabsConfig(true)}
              className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm text-white flex items-center gap-2"
            >
              <Edit2 size={14} />
              Configurer
            </button>
          </div>
        </div>
        
        {/* Aperçu des onglets */}
        <div className="p-3 flex flex-wrap gap-2">
          {dynamicTabs.filter(t => t.enabled).sort((a, b) => a.order - b.order).map(tab => (
            <span key={tab.id} className="px-3 py-1.5 bg-indigo-500/20 border border-indigo-500/30 rounded-lg text-sm text-indigo-300 flex items-center gap-2">
              {tab.label}
              {tab.badge_field && <span className="w-4 h-4 rounded-full bg-orange-500 text-[10px] text-white flex items-center justify-center">0</span>}
            </span>
          ))}
          {dynamicTabs.filter(t => t.enabled).length === 0 && (
            <span className="text-sm text-slate-500 italic">Aucun onglet configuré</span>
          )}
        </div>
      </div>

      {/* Boutons IA globaux */}
      <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <div>
              <h4 className="font-medium text-white">Suggestions IA</h4>
              <p className="text-sm text-slate-400">Laissez l'IA vous proposer des designs optimisés</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => askAiSuggestion('sections')}
              disabled={aiSuggesting}
              className="px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-lg text-sm text-purple-300 flex items-center gap-2 disabled:opacity-50"
            >
              {aiSuggesting ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
              Suggérer sections
            </button>
            <button
              onClick={() => askAiSuggestion('full')}
              disabled={aiSuggesting}
              className="px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 rounded-lg text-sm text-white flex items-center gap-2 disabled:opacity-50"
            >
              {aiSuggesting ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Design complet
            </button>
          </div>
        </div>
      </div>

      {/* Titre sections */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Settings size={20} className="text-slate-400" />
          Sections du modal
        </h3>
        <span className="text-sm text-slate-400">{sections.length} section(s)</span>
      </div>
      
      {/* Liste des sections avec drag & drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sections.map(s => s.id)}
          strategy={verticalListSortingStrategy}
        >
          {sections.map(section => (
            <SortableSection
              key={section.id}
              section={section}
              isExpanded={expandedSections.has(section.id)}
              onToggle={toggleSectionExpand}
              onEdit={openEditSectionModal}
              onDelete={deleteSection}
              onToggleField={toggleField}
              onAddField={openAddFieldModal}
              onDeleteField={deleteField}
              onMoveField={moveField}
              onRenameField={renameField}
            />
          ))}
        </SortableContext>
      </DndContext>
      
      {/* Bouton ajouter section */}
      <button
        onClick={() => setShowAddSectionModal(true)}
        className="w-full p-4 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:text-white hover:border-purple-500 flex items-center justify-center gap-2 transition-colors"
      >
        <Plus size={20} />
        Ajouter une section
      </button>
      
      {/* Modales */}
      <AddSectionModal
        isOpen={showAddSectionModal}
        onClose={() => setShowAddSectionModal(false)}
        onSave={addSection}
        sheetColumns={sheetColumns}
      />
      
      <AddFieldModal
        isOpen={showAddFieldModal}
        onClose={() => setShowAddFieldModal(false)}
        onSave={addField}
        sectionId={currentSectionForField}
        sheetColumns={sheetColumns}
        existingFieldIds={getExistingFieldIds(currentSectionForField)}
        allUsedColumns={getAllUsedColumns()}
      />
      
      <EditSectionModal
        isOpen={showEditSectionModal}
        onClose={() => { setShowEditSectionModal(false); setSectionToEdit(null); }}
        section={sectionToEdit}
        onSave={updateSection}
      />
      
      <HeaderConfigModal
        isOpen={showHeaderConfig}
        onClose={() => setShowHeaderConfig(false)}
        headerConfig={headerConfig}
        onSave={handleUpdateHeader}
      />
      
      <DynamicTabsConfigModal
        isOpen={showTabsConfig}
        onClose={() => setShowTabsConfig(false)}
        tabs={dynamicTabs}
        onSave={handleUpdateTabs}
      />
      
      {/* Modal de prévisualisation */}
      <ModalPreview
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        headerConfig={headerConfig}
        dynamicTabs={dynamicTabs}
        sections={sections}
        templateName={selectedTemplate?.name || 'Template'}
      />
    </div>
  );
}
